import { useEffect, useState } from 'react'
import type { IOrder } from '../../../../Model/index.js'
import type { IEvaluationProject } from '../../../../Model/Project/IEvaluationProject.js'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'

export interface KronaModalProps {
  order?: IOrder
  project?: IEvaluationProject
}

export const KronaModal = ({ isOpen, onClose, order, project }: KronaModalProps & BaseModalProps) => {
  const [kronaEmbedUrl, setKronaEmbedUrl] = useState('')

  const [isIframeReady, setIsIframeReady] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'krona-ready') {
        setHasFailed(false)
        setIsIframeReady(true)
      }
      /* without this the spinner would keep running forever on a failed embed */
      if (event.data === 'krona-error') {
        setHasFailed(true)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  useEffect(() => {
    const contextId = order?.id ?? project?.id
    const isProject = !!project?.id

    setKronaEmbedUrl(`/krona-embed/${contextId}?isProject=${isProject}`)
  }, [order, project])

  /* a reopened modal loads a fresh iframe, so it must not inherit the previous attempt's verdict */
  useEffect(() => {
    if (!isOpen) {
      setIsIframeReady(false)
      setHasFailed(false)
    }
  }, [isOpen])

  return (
    <BaseModal onClose={onClose} isOpen={isOpen}>
      <div className="modal-box w-screen max-w-7xl h-full p-6 overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">View Krona Plot</h3>
          <button
            className="btn btn-circle btn-ghost mr-2"
            onClick={() => {
              onClose()
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="form-control w-full h-[90%]">
          {isOpen && (
            <div className="relative w-full h-full">
              {!isIframeReady && (
                <div className="absolute inset-0 bg-base-100/50 flex items-center justify-center z-10">
                  {hasFailed ? (
                    <span className="text-sm text-error">Could not load the Krona plot. Please close this dialog and try again.</span>
                  ) : (
                    <span className="loading loading-spinner loading-lg"></span>
                  )}
                </div>
              )}

              <iframe
                src={kronaEmbedUrl}
                className={`w-full h-full border rounded-sm transition-opacity duration-300 ${isIframeReady ? 'opacity-100' : 'opacity-0'}`}
              />
            </div>
          )}
        </div>
        <div className={'text-xs'}>
          <a target={'_blank'} rel="noopener noreferrer" href={'https://github.com/marbl/Krona/blob/master/KronaTools/LICENSE.txt'}>
            Powered by KronaTools - view License
          </a>
        </div>
      </div>
    </BaseModal>
  )
}
